import { BATTLE_RULE_SET_ID, type BattleState } from '../rules/battleRuleSet';
import type { Direction, GameInput } from '../rules/contracts';
import { createRng, type Rng } from '../rng';
import { GAME_MODE, type GameModeId, type Timestamp } from '../types';
import { chooseBotMove } from './botBrain';
import {
  botDifficultySettings,
  parseBotDifficulty,
  type BotDifficulty,
  type BotDifficultySettings,
} from './botDifficulty';
import type { GameModeDefinition, ModeSetup, ModeStep } from './GameMode';
import { VersusMode } from './VersusMode';

/**
 * One connected human versus a deterministic server-controlled second seat.
 *
 * The difficulty arrives through `ModeSetup.config`, which is the channel the
 * contract already reserves for mode configuration — so the room passes a
 * player's choice down without the mode learning anything about rooms, and the
 * predicting client constructs the same mode from the same config.
 *
 * Determinism comes from the match seed. The bot's randomness is a seeded
 * stream, not `Math.random()`, so replaying a match seed replays the opponent —
 * which is what makes a bot match reproducible when somebody reports that it
 * did something absurd.
 */
export class BotMode extends VersusMode {
  override readonly id: GameModeId = GAME_MODE.BOT;
  override readonly requiredSeats = 1;

  private difficulty: BotDifficulty = parseBotDifficulty(undefined);
  private settings: BotDifficultySettings = botDifficultySettings(this.difficulty);
  private random: Rng = createRng('bot');
  private nextBotMoveAt = 0;

  override init(setup: ModeSetup): void {
    super.init(setup);

    this.difficulty = parseBotDifficulty(
      (setup.config as { difficulty?: unknown } | undefined)?.difficulty,
    );
    this.settings = botDifficultySettings(this.difficulty);
    // Salted so the bot's stream cannot walk in step with the board's spawns,
    // which share the same match seed.
    this.random = createRng(`${setup.seed}:bot:${this.difficulty}`);
    this.nextBotMoveAt = setup.startedAt + this.settings.moveIntervalMs;
  }

  /** Which opponent this is. The room reads it to name the seat. */
  get botDifficulty(): BotDifficulty {
    return this.difficulty;
  }

  override tick(now: Timestamp): ModeStep | null {
    if (this.isFinished() || now < this.nextBotMoveAt) return null;
    this.nextBotMoveAt = now + this.settings.moveIntervalMs;

    const battle = this.state as BattleState;
    const bot = battle.players.find((player) => player.seat === 1);
    if (!bot) return null;

    const direction = chooseBotMove(bot.board, {
      depth: this.settings.searchDepth,
      blunderChance: this.settings.blunderChance,
      moves: bot.moves,
      random: this.random,
    });
    if (!direction) return null;

    const input: GameInput = {
      kind: 'swipe',
      seat: bot.seat,
      seq: bot.lastInputSeq + 1,
      at: now,
      direction,
    };
    return super.handleInput(input, now);
  }
}

/**
 * The move the bot would make right now, at its default strength.
 *
 * Kept because it is the shape the tests and any future replay tooling want:
 * one board in, one direction out, no mode instance and no clock.
 */
export function chooseBotDirection(state: BattleState): Direction | null {
  const bot = state.players.find((player) => player.seat === 1);
  if (!bot) return null;

  const settings = botDifficultySettings(parseBotDifficulty(undefined));
  return chooseBotMove(bot.board, {
    depth: settings.searchDepth,
    blunderChance: 0,
    moves: bot.moves,
    random: () => 0,
  });
}

export const botModeDefinition: GameModeDefinition = {
  id: GAME_MODE.BOT,
  requiredSeats: 1,
  defaultRuleSetId: BATTLE_RULE_SET_ID,
  create: (ruleSetId = BATTLE_RULE_SET_ID) => new BotMode(ruleSetId),
};
